import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dimmer,
  Dropdown,
  Grid,
  Icon,
  Input,
  Label,
  Loader,
  Message,
  Modal,
} from 'semantic-ui-react';
import styled from 'styled-components';
import Button from '../../../../components/Button';
import { FieldLabel } from '../../../../styles';
import {
  dangerNotification,
  successNotification,
} from '../../../../services/notification';
import {
  markBatchTransmitted,
  updateBatchCodes,
  UpdatedBatchCodesResponse,
} from '../../../../services/fire-insurance-batches';
import {
  BatchStatusOptions,
  getBatchStatusColor,
  translateBatchStatus,
} from '../types';
import {
  BatchCodesFormErrors,
  BatchCodesFormValues,
  buildFormValuesFromBatch,
  buildQuotationCode,
  hasBatchCodesChanged,
  parseQuotationCode,
  POLICY_NUMBER_REGEX,
  PROPOSAL_CODE_REGEX,
  QUOTE_NO_REGEX,
  QUOTE_YEAR_REGEX,
  stripDigits,
  validateBatchCodesForm,
} from '../utils/batch-codes.validation';

export interface EditBatchCodesModalBatch {
  id: string;
  batchNumber: number;
  quotationCode: string;
  proposalCode: string | null;
  policyNumber: string | null;
  status: string;
  insurer?: {
    code?: string;
  };
  estate: {
    name: string;
  };
}

interface EditBatchCodesModalProps {
  open: boolean;
  batch: EditBatchCodesModalBatch | null;
  onClose: () => void;
  onSuccess: (updated: UpdatedBatchCodesResponse) => void;
}

const ContextBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 16px;
`;

const ContextItem = styled.span`
  color: #444;
  font-size: 13px;

  strong {
    color: #333;
  }
`;

const SectionTitle = styled.div`
  color: #59249b;
  font-size: 13px;
  font-weight: 600;
  margin: 16px 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
`;

const FieldHint = styled.span<{ $error?: boolean }>`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ $error }) => ($error ? '#db2828' : '#888')};
`;

const PreviewText = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #555;

  strong {
    color: #333;
  }
`;

const QuotationRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const QuotationField = styled.div`
  flex: 1;
`;

const QuotationSeparator = styled.span`
  color: #999;
  font-size: 20px;
  line-height: 38px;
  padding-top: 22px;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const ClearLink = styled.button`
  border: 0;
  background: transparent;
  color: #59249b;
  cursor: pointer;
  font-size: 12px;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: #bbb;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const OptionalHint = styled.span`
  color: #888;
  font-size: 12px;
  font-weight: normal;
`;

const EditBatchCodesModal: React.FC<EditBatchCodesModalProps> = ({
  open,
  batch,
  onClose,
  onSuccess,
}) => {
  const quoteNoInputRef = useRef<HTMLInputElement>(null);
  const quoteYearInputRef = useRef<HTMLInputElement>(null);

  const [values, setValues] = useState<BatchCodesFormValues>({
    quoteNo: '',
    quoteYear: '',
    proposalCode: '',
    policyNumber: '',
    status: '',
  });
  const [initialValues, setInitialValues] = useState<BatchCodesFormValues>({
    quoteNo: '',
    quoteYear: '',
    proposalCode: '',
    policyNumber: '',
    status: '',
  });
  const [errors, setErrors] = useState<BatchCodesFormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof BatchCodesFormValues, boolean>>>({});
  const [loading, setLoading] = useState(false);
  const [transmitting, setTransmitting] = useState(false);
  const [hasLegacyQuotation, setHasLegacyQuotation] = useState(false);

  const resetForm = useCallback((source: EditBatchCodesModalBatch) => {
    const parsed = parseQuotationCode(source.quotationCode || '');
    setHasLegacyQuotation(Boolean(source.quotationCode) && !parsed);

    const formValues = buildFormValuesFromBatch(source);
    setValues(formValues);
    setInitialValues(formValues);
    setErrors({});
    setTouched({});
  }, []);

  useEffect(() => {
    if (open && batch) {
      resetForm(batch);
    }
  }, [open, batch, resetForm]);

  useEffect(() => {
    if (open) {
      const timer = window.setTimeout(() => {
        quoteNoInputRef.current?.focus();
      }, 100);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open, batch?.id]);

  const isDirty = useMemo(
    () => hasBatchCodesChanged(initialValues, values),
    [initialValues, values],
  );

  const quotationPreview = useMemo(() => {
    if (!values.quoteNo && !values.quoteYear) {
      return null;
    }
    if (values.quoteNo.length === 6 && values.quoteYear.length === 4) {
      return buildQuotationCode(values.quoteNo, values.quoteYear);
    }
    return `${values.quoteNo || '______'}-${values.quoteYear || '____'}`;
  }, [values.quoteNo, values.quoteYear]);

  const validateField = async (
    field: keyof BatchCodesFormValues,
    nextValues: BatchCodesFormValues,
  ) => {
    const fieldErrors = await validateBatchCodesForm(nextValues);
    setErrors((prev) => ({
      ...prev,
      [field]: fieldErrors[field],
    }));
  };

  const handleNumericChange = (
    field: keyof BatchCodesFormValues,
    rawValue: string,
    maxLength: number,
  ) => {
    const nextValue = stripDigits(rawValue, maxLength);
    const nextValues = { ...values, [field]: nextValue };
    setValues(nextValues);

    if (field === 'quoteNo' && nextValue.length === 6) {
      quoteYearInputRef.current?.focus();
    }

    if (touched[field]) {
      void validateField(field, nextValues);
    }
  };

  const handleBlur = (field: keyof BatchCodesFormValues) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    void validateField(field, values);
  };

  const handleStatusChange = (status: string) => {
    const nextValues = { ...values, status };
    setValues(nextValues);
    setTouched((prev) => ({ ...prev, status: true }));
    void validateField('status', nextValues);
  };

  const handleClearOptional = (field: 'proposalCode' | 'policyNumber') => {
    const nextValues = { ...values, [field]: '' };
    setValues(nextValues);
    setTouched((prev) => ({ ...prev, [field]: true }));
    void validateField(field, nextValues);
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    if (isDirty && !window.confirm('Descartar alterações?')) {
      return;
    }

    onClose();
  };

  const handleSubmit = async () => {
    if (!batch) {
      return;
    }

    const allTouched: Partial<Record<keyof BatchCodesFormValues, boolean>> = {
      quoteNo: true,
      quoteYear: true,
      proposalCode: true,
      policyNumber: true,
      status: true,
    };
    setTouched(allTouched);

    const validationErrors = await validateBatchCodesForm(values);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    if (!isDirty) {
      return;
    }

    setLoading(true);
    try {
      const updated = await updateBatchCodes(batch.id, {
        quotationCode: buildQuotationCode(values.quoteNo, values.quoteYear),
        proposalCode: values.proposalCode || null,
        policyNumber: values.policyNumber || null,
        status: values.status,
      });

      if (updated.quoteSyncQueued) {
        successNotification(
          'Sucesso',
          'Lote salvo. Sincronização com a Yelum enfileirada; você receberá um email com o relatório.',
        );
      } else {
        successNotification('Sucesso', 'Lote atualizado com sucesso');
      }
      onSuccess(updated);
      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Erro ao atualizar códigos do lote';

      if (message.toLowerCase().includes('cotação')) {
        setErrors((prev) => ({ ...prev, quoteNo: message }));
        setTouched((prev) => ({ ...prev, quoteNo: true, quoteYear: true }));
      }

      dangerNotification('Erro', message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkTransmitted = async () => {
    if (!batch || isDirty) {
      return;
    }

    setTransmitting(true);
    try {
      const updated = await markBatchTransmitted(batch.id);
      successNotification(
        'Sucesso',
        'Transmissão manual registrada. A próxima transmissão foi agendada para o próximo ciclo.',
      );
      onSuccess(updated);
      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Erro ao registrar a transmissão manual';
      dangerNotification('Erro', message);
    } finally {
      setTransmitting(false);
    }
  };

  const isFormValid = useMemo(
    () =>
      QUOTE_NO_REGEX.test(values.quoteNo) &&
      QUOTE_YEAR_REGEX.test(values.quoteYear) &&
      Boolean(values.status) &&
      (!values.proposalCode || PROPOSAL_CODE_REGEX.test(values.proposalCode)) &&
      (!values.policyNumber || POLICY_NUMBER_REGEX.test(values.policyNumber)),
    [values],
  );

  if (!batch) {
    return null;
  }

  return (
    <Modal open={open} onClose={handleClose} size="small" closeOnDimmerClick={!isDirty}>
      <Modal.Header>
        <Icon name="edit" />
        Editar lote
      </Modal.Header>

      <Modal.Content>
        <Dimmer active={loading || transmitting} inverted>
          <Loader indeterminate>
            {transmitting ? 'Registrando transmissão...' : 'Salvando...'}
          </Loader>
        </Dimmer>

        <ContextBanner>
          <ContextItem>
            <strong>Lote #{batch.batchNumber}</strong>
          </ContextItem>
          <ContextItem>·</ContextItem>
          <ContextItem>{batch.estate.name}</ContextItem>
          <ContextItem>·</ContextItem>
          <Label color={getBatchStatusColor(values.status || batch.status) as any} size="tiny">
            {translateBatchStatus(values.status || batch.status)}
          </Label>
        </ContextBanner>

        <SectionTitle>Status do lote</SectionTitle>
        <FieldLabel>Status atual *</FieldLabel>
        <Dropdown
          fluid
          selection
          options={BatchStatusOptions}
          value={values.status}
          disabled={loading}
          error={Boolean(touched.status && errors.status)}
          onChange={(_, { value }) => handleStatusChange(String(value))}
          onBlur={() => handleBlur('status')}
          placeholder="Selecione o status"
        />
        <FieldHint $error={Boolean(touched.status && errors.status)}>
          {touched.status && errors.status ? errors.status : ' '}
        </FieldHint>

        {hasLegacyQuotation ? (
          <Message warning icon size="small">
            <Icon name="warning sign" />
            <Message.Content>
              Cotação atual em formato não reconhecido. Informe os valores corretos.
            </Message.Content>
          </Message>
        ) : null}

        <SectionTitle>Cotação Yelum</SectionTitle>
        <QuotationRow>
          <QuotationField>
            <FieldLabel>Número da cotação *</FieldLabel>
            <Input
              input={{ ref: quoteNoInputRef }}
              fluid
              value={values.quoteNo}
              inputMode="numeric"
              maxLength={6}
              disabled={loading}
              error={Boolean(touched.quoteNo && errors.quoteNo)}
              onChange={(_, { value }) => handleNumericChange('quoteNo', String(value), 6)}
              onBlur={() => handleBlur('quoteNo')}
              placeholder="123456"
            />
            <FieldHint $error={Boolean(touched.quoteNo && errors.quoteNo)}>
              {touched.quoteNo && errors.quoteNo
                ? errors.quoteNo
                : `${values.quoteNo.length}/6 dígitos`}
            </FieldHint>
          </QuotationField>

          <QuotationSeparator>-</QuotationSeparator>

          <QuotationField>
            <FieldLabel>Ano *</FieldLabel>
            <Input
              input={{ ref: quoteYearInputRef }}
              fluid
              value={values.quoteYear}
              inputMode="numeric"
              maxLength={4}
              disabled={loading}
              error={Boolean(touched.quoteYear && errors.quoteYear)}
              onChange={(_, { value }) => handleNumericChange('quoteYear', String(value), 4)}
              onBlur={() => handleBlur('quoteYear')}
              placeholder="2025"
            />
            <FieldHint $error={Boolean(touched.quoteYear && errors.quoteYear)}>
              {touched.quoteYear && errors.quoteYear
                ? errors.quoteYear
                : `${values.quoteYear.length}/4 dígitos`}
            </FieldHint>
          </QuotationField>
        </QuotationRow>

        {quotationPreview ? (
          <PreviewText>
            Prévia: <strong>{quotationPreview}</strong>
          </PreviewText>
        ) : null}

        <SectionTitle>Identificadores</SectionTitle>

        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <LabelRow>
                <FieldLabel>
                  Código de proposta <OptionalHint>(opcional)</OptionalHint>
                </FieldLabel>
                {values.proposalCode ? (
                  <ClearLink
                    type="button"
                    disabled={loading}
                    onClick={() => handleClearOptional('proposalCode')}
                  >
                    Limpar
                  </ClearLink>
                ) : null}
              </LabelRow>
              <Input
                fluid
                value={values.proposalCode}
                inputMode="numeric"
                maxLength={9}
                disabled={loading}
                error={Boolean(touched.proposalCode && errors.proposalCode)}
                onChange={(_, { value }) =>
                  handleNumericChange('proposalCode', String(value), 9)
                }
                onBlur={() => handleBlur('proposalCode')}
                placeholder="533633227"
              />
              <FieldHint $error={Boolean(touched.proposalCode && errors.proposalCode)}>
                {touched.proposalCode && errors.proposalCode
                  ? errors.proposalCode
                  : `${values.proposalCode.length}/9 dígitos`}
              </FieldHint>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16}>
              <LabelRow>
                <FieldLabel>
                  Número da apólice <OptionalHint>(opcional)</OptionalHint>
                </FieldLabel>
                {values.policyNumber ? (
                  <ClearLink
                    type="button"
                    disabled={loading}
                    onClick={() => handleClearOptional('policyNumber')}
                  >
                    Limpar
                  </ClearLink>
                ) : null}
              </LabelRow>
              <Input
                fluid
                value={values.policyNumber}
                inputMode="numeric"
                maxLength={15}
                disabled={loading}
                error={Boolean(touched.policyNumber && errors.policyNumber)}
                onChange={(_, { value }) =>
                  handleNumericChange('policyNumber', String(value), 15)
                }
                onBlur={() => handleBlur('policyNumber')}
                placeholder="142520260688467"
              />
              <FieldHint $error={Boolean(touched.policyNumber && errors.policyNumber)}>
                {touched.policyNumber && errors.policyNumber
                  ? errors.policyNumber
                  : `${values.policyNumber.length}/15 dígitos`}
              </FieldHint>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Message info size="small" style={{ marginTop: 16, marginBottom: 0 }}>
          <Icon name="info circle" />
          Esses códigos são usados na integração com a Yelum e na sincronização por planilha.
        </Message>
      </Modal.Content>

      <Modal.Actions>
        {batch.insurer?.code === 'YELUM' && (
          <Button
            basic
            color="orange"
            onClick={handleMarkTransmitted}
            loading={transmitting}
            disabled={loading || transmitting || isDirty}
            title={
              isDirty
                ? 'Salve ou descarte as alterações dos códigos antes de registrar a transmissão'
                : 'Registra que o lote foi transmitido manualmente e agenda o próximo ciclo'
            }
          >
            <Icon name="check circle" />
            Transmitido manualmente
          </Button>
        )}
        <Button basic onClick={handleClose} disabled={loading || transmitting}>
          Cancelar
        </Button>
        <Button
          color="purple"
          onClick={handleSubmit}
          loading={loading}
          disabled={loading || transmitting || !isDirty || !isFormValid}
        >
          Salvar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditBatchCodesModal;
